import { useEffect, useMemo, useState } from "react";
import PerceptualResponseInput from "../../primitives/perceptual-response/PerceptualResponseInput";
import { useOptionalTaskRuntime } from "../../context/TaskRuntimeContext";
import PresentedStimulusAudio from "../../components/PresentedStimulusAudio";
import { ApplicationButton, ApplicationSurface } from "../../../../components/application";

const answered = value => value !== undefined && value !== null && value !== "";

export default function ComposablePerceptualTrialTask({ task, session, responses, onResponse }) {
  const runtime = useOptionalTaskRuntime();
  task = task || runtime?.task;
  session = session || runtime?.session;
  const contract = task?.perceptualTrial || session?.activityExecutionSnapshot?.perceptualTrial;
  const supplementalGuidance = task?.supplementalGuidance || session?.activityExecutionSnapshot?.activitySemantics?.supplementalGuidance || "";
  const [locallyAnswered, setLocallyAnswered] = useState([]);
  const answeredTrials = useMemo(() => {
    const persistedResponses = responses || runtime?.responses || [];
    return new Set([...persistedResponses.map(response => String(response.trialId || response.trialKey)), ...locallyAnswered]);
  }, [responses, runtime?.responses, locallyAnswered]);
  const trial = session?.presentedTrials?.find(item => !answeredTrials.has(String(item.trialId)) && !answeredTrials.has(String(item.trialKey)));
  const items = useMemo(() => [...(contract?.judgmentItems || [])].sort((a, b) => a.order - b.order), [contract]);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => setValues({}), [trial?.trialKey, trial?.trialId]);
  const set = (key, value) => setValues(current => ({ ...current, [key]: value }));
  const submit = async () => {
    setSubmitting(true);
    try {
    const payload = {
    sessionId: session?._id,
    trialId: trial?.trialId || trial?._id,
    trialKey: trial?.trialKey,
    stimulusInstanceId: trial?.stimuli?.find(stimulus => stimulus.slotKey === "stimulus")?.stimulusInstanceId,
    responseType: "structured",
    response: {
      contractVersion: "perceptual_trial_response.v1",
      itemResponses: items.map(item => ({ itemKey: item.itemKey,
        primitive: { key: item.primitive.key, version: item.primitive.version }, value: values[item.itemKey] })),
    },
    };
    if (onResponse) await onResponse(payload);
    else {
      const result = await fetch(`${runtime.apiUrl}/evaluation/response`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${runtime.token}` }, body: JSON.stringify(payload),
      });
      if (!result.ok) throw new Error("PERCEPTUAL_RESPONSE_SAVE_FAILED");
    }
    const identity = String(trial?.trialId || trial?.trialKey);
    const nextAnswered = new Set([...answeredTrials, identity]);
    setLocallyAnswered(current => [...current, identity]);
    if ((session?.presentedTrials || []).every(item => nextAnswered.has(String(item.trialId)) || nextAnswered.has(String(item.trialKey)))) {
      await runtime?.completeCurrentStep?.(session._id);
    }
    } finally {
      setSubmitting(false);
    }
  };
  if (!trial) return <section aria-label="Perceptual trial"><p>All presented trials are complete.</p></section>;
  return <ApplicationSurface className="av-perceptual-trial" aria-label="Perceptual trial">
    <section aria-labelledby="presented-stimulus-heading">
      <h2 id="presented-stimulus-heading">Presented stimulus</h2>
      <PresentedStimulusAudio trial={trial} />
    </section>
    {supplementalGuidance ? <aside className="av-perceptual-trial__guidance" aria-label="Activity guidance"><p>{supplementalGuidance}</p></aside> : null}
    {items.map(item => {
      const { key, config } = item.primitive;
      if (key === "continuous_numeric") return <PerceptualResponseInput key={item.itemKey}
        constructLabel={item.target.name} instruction={item.target.instruction} scaleFamily="continuous"
        min={config.min} max={config.max} value={values[item.itemKey]} onChange={value => set(item.itemKey, value)}
        endpointLabels={{ min: config.endpointLabels.left, max: config.endpointLabels.right }} />;
      if (key === "bounded_free_text") return <label key={item.itemKey}>{item.target.name}
        <span>{item.target.instruction}</span><textarea maxLength={config.maxLength} value={values[item.itemKey] || ""}
          onChange={event => set(item.itemKey, event.target.value)} /></label>;
      if (!["ordinal_categorical", "nominal_categorical", "binary_choice"].includes(key)) {
        return <p role="alert" key={item.itemKey}>This response type is not supported.</p>;
      }
      return <fieldset key={item.itemKey}><legend>{item.target.name}</legend><p>{item.target.instruction}</p>
        {config.options.map(option => <label key={option.key}><input type="radio" name={item.itemKey}
          checked={values[item.itemKey] === option.key} onChange={() => set(item.itemKey, option.key)} />{option.label}</label>)}</fieldset>;
    })}
    <ApplicationButton type="button" disabled={submitting || !items.length || items.some(item => !answered(values[item.itemKey]))} onClick={submit}>Submit responses</ApplicationButton>
  </ApplicationSurface>;
}
